/** Pure helpers for the invite popup: place tree, role per place, and the summary line. */

export interface PlaceTarget {
  id: number;
  name: string;
  kind: string;
}

interface IndexNode {
  id: number;
  name: string;
  kind: string;
  parent_id?: number | null;
}

export interface PlaceTreeNode {
  value: number;
  name: string;
  kind: string;
  /** False for an ancestor shown only to give context; the inviter may not grant on it. */
  selectable: boolean;
  children: PlaceTreeNode[];
}

/**
 * Nest the places the inviter may grant on under their ancestors from the context tree.
 * An ancestor that is not itself a target is kept (unselectable) so the hierarchy reads;
 * a target whose parent is unknown becomes a root.
 */
export const buildPlaceTree = (
  targets: PlaceTarget[],
  index: Record<number, IndexNode>
): PlaceTreeNode[] => {
  const allowed = new Set(targets.map((target) => target.id));
  const byId = new Map<number, PlaceTreeNode>();
  const roots: PlaceTreeNode[] = [];

  const ensure = (id: number, fallback?: PlaceTarget): PlaceTreeNode | undefined => {
    const existing = byId.get(id);
    if (existing) return existing;
    const source = index[id] ?? fallback;
    if (!source) return undefined;
    const node: PlaceTreeNode = {
      value: id,
      name: source.name,
      kind: source.kind,
      selectable: allowed.has(id),
      children: []
    };
    byId.set(id, node);
    const parentId = index[id]?.parent_id;
    const parent = parentId != null && index[parentId] ? ensure(parentId) : undefined;
    (parent ? parent.children : roots).push(node);
    return node;
  };

  targets.forEach((target) => {
    ensure(target.id, target);
  });
  return roots;
};

/** The role a place gets: its own override, else the default for everyone. */
export const effectiveRole = (
  nodeId: number,
  defaultRole: string | undefined,
  overrides: Record<number, string | undefined>
): string | undefined => overrides[nodeId] ?? defaultRole;

/** Chosen places whose effective role is missing or not offered for that place. */
export const placesNeedingRole = (
  nodeIds: number[],
  defaultRole: string | undefined,
  overrides: Record<number, string | undefined>,
  offered: (nodeId: number) => string[] | undefined
): number[] =>
  nodeIds.filter((id) => {
    const role = effectiveRole(id, defaultRole, overrides);
    if (!role) return true;
    const list = offered(id);
    return list !== undefined && !list.includes(role);
  });

const PLURAL: Record<string, string> = {
  organisation: 'organisations',
  entity: 'entities',
  workspace: 'workspaces'
};

export interface SummaryPlace {
  name: string;
  kind: string;
  /** Display name of the effective role. */
  roleName?: string;
}

/**
 * "Viewer on 3 workspaces, Admin on Entity 1": places are grouped by role, then by kind;
 * a single place is named, several of one kind are counted. Places with no role are left out.
 */
export const buildSummary = (places: SummaryPlace[]): string => {
  const byRole = new Map<string, Map<string, string[]>>();
  places.forEach((place) => {
    if (!place.roleName) return;
    const kinds = byRole.get(place.roleName) ?? new Map<string, string[]>();
    kinds.set(place.kind, [...(kinds.get(place.kind) ?? []), place.name]);
    byRole.set(place.roleName, kinds);
  });
  const parts: string[] = [];
  byRole.forEach((kinds, roleName) => {
    const where: string[] = [];
    kinds.forEach((names, kind) => {
      where.push(names.length === 1 ? names[0] : `${names.length} ${PLURAL[kind] ?? 'places'}`);
    });
    parts.push(`${roleName} on ${where.join(' and ')}`);
  });
  return parts.join(', ');
};

/**
 * Places that get their own role row: only those the user chose to override, plus any place
 * where the default role is not offered (so a role must be picked). Never a row for a single place.
 */
export const placesWithRow = (
  nodeIds: number[],
  defaultRole: string | undefined,
  overrides: Record<number, string | undefined>,
  offered: (nodeId: number) => string[] | undefined,
  overriding: Record<number, boolean>
): number[] => {
  if (nodeIds.length < 2) return [];
  const needing = new Set(placesNeedingRole(nodeIds, defaultRole, overrides, offered));
  return nodeIds.filter((id) => overriding[id] || overrides[id] !== undefined
    || (defaultRole !== undefined && needing.has(id)));
};

/** Keep overrides only for chosen places; none at all when fewer than 2 places remain. */
export const pruneOverrides = <T>(
  nodeIds: number[],
  overrides: Record<number, T>
): Record<number, T> => {
  if (nodeIds.length < 2) return {};
  const kept = new Set(nodeIds);
  return Object.fromEntries(
    Object.entries(overrides).filter(([id]) => kept.has(Number(id)))
  ) as Record<number, T>;
};

/** Chosen places where the default role is not offered, so the user must pick another there. */
export const placesRefusingDefault = (
  nodeIds: number[],
  defaultRole: string | undefined,
  offered: (nodeId: number) => string[] | undefined
): number[] => {
  if (defaultRole === undefined) return [];
  return nodeIds.filter((id) => {
    const list = offered(id);
    return list !== undefined && !list.includes(defaultRole);
  });
};

/** A fresh override starts at the default role (never another), or empty when not offered there. */
export const startingOverride = (
  nodeId: number,
  defaultRole: string | undefined,
  offered: (nodeId: number) => string[] | undefined
): string | undefined => {
  const list = offered(nodeId);
  return defaultRole !== undefined && list?.includes(defaultRole) ? defaultRole : undefined;
};
